// Framing helper for task-detail overhead images.
//
// A "frame" is a normalized source rectangle — { x, y, width, height } with
// every value in [0..1] relative to the image's natural size — that a renderer
// draws to fill its viewport. Keeping the output normalized (not pixels) lets
// web (canvas drawImage source rect) and mobile (<Image> crop/transform) share
// the exact same math.
//
// Bbox comes from `metadata` as pixel corners { x1, y1, x2, y2 } in the image's
// natural dimensions (not normalized).

// Fraction of the image trimmed off one end in 'auto' framing. Single tunable
// source of truth for the overhead crop amount.
export const AUTO_CROP_RATIO = 0.4;

// Safety gap kept between the crop line and the detection box, as a fraction of
// image height. The trim is capped so it never cuts into the bbox (+ this margin
// of breathing room). This is the "bbox protection".
export const BBOX_SAFE_MARGIN = 0.03;

const FULL_RECT = { x: 0, y: 0, width: 1, height: 1 };

function hasBbox(metadata) {
  return (
    metadata != null &&
    metadata.x1 != null &&
    metadata.y1 != null &&
    metadata.x2 != null &&
    metadata.y2 != null
  );
}

/**
 * Compute the normalized source rectangle for a framing mode.
 *
 * 'full' returns the whole image. 'auto' trims AUTO_CROP_RATIO off one vertical
 * end — the top when the bbox is in the lower half (or there is no bbox), the
 * bottom when the bbox is in the upper half — but never cuts into the detection
 * box: the trim is capped so the crop line stays BBOX_SAFE_MARGIN away from the
 * near bbox edge.
 *
 * @param {string} mode 'auto' or 'full'.
 * @param {{x1:number,y1:number,x2:number,y2:number}|null|undefined} metadata Bbox in natural pixels.
 * @param {number} naturalWidth Image natural width in pixels (unused; kept for the shared signature).
 * @param {number} naturalHeight Image natural height in pixels.
 * @returns {{x:number,y:number,width:number,height:number}} Normalized source rect in [0..1].
 */
export function getFrameSourceRect(mode, metadata, naturalWidth, naturalHeight) {
  if (mode !== 'auto' || !naturalHeight) return FULL_RECT;

  if (!hasBbox(metadata)) {
    // No detection -> trim the (least informative) top by the full ratio.
    return { x: 0, y: AUTO_CROP_RATIO, width: 1, height: 1 - AUTO_CROP_RATIO };
  }

  const topFraction = metadata.y1 / naturalHeight;
  const bottomFraction = metadata.y2 / naturalHeight;
  const centerFraction = (metadata.y1 + metadata.y2) / 2 / naturalHeight;

  if (centerFraction >= 0.5) {
    // Product low -> trim the top, but not past the bbox top edge (- margin).
    const cut = Math.min(AUTO_CROP_RATIO, Math.max(0, topFraction - BBOX_SAFE_MARGIN));
    return { x: 0, y: cut, width: 1, height: 1 - cut };
  }
  // Product high -> trim the bottom, but not past the bbox bottom edge (+ margin).
  const cut = Math.min(AUTO_CROP_RATIO, Math.max(0, 1 - (bottomFraction + BBOX_SAFE_MARGIN)));
  return { x: 0, y: 0, width: 1, height: 1 - cut };
}
